import React, { useEffect, useState } from 'react';
import {
  FormSchema,
  FormField,
  FieldType,
} from '../../../types/FormSchema';
import {
  Box,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  Container,
  Chip,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SignatureField from '../SignatureField';
import {
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Language as WebIcon,
  MusicNote as SpotifyIcon,
  Album as BandcampIcon,
  Apple as AppleMusicIcon,
} from '@mui/icons-material';

interface FormViewerProps {
  schema: FormSchema;
  values: Record<string, any>;
  onChange: (fieldId: string, value: any) => void;
  disabled?: boolean;
}

// Link detection helper functions
const detectPlatform = (url: string): { platform: string; color: string; icon: React.ReactElement; thumbnail?: string; embedType?: 'spotify' | 'bandcamp' } | null => {
  if (!url) return null;
  
  const urlLower = url.toLowerCase();
  
  // YouTube - extract video ID and generate thumbnail
  if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) {
    let videoId = '';
    if (urlLower.includes('youtube.com/watch')) {
      const match = url.match(/[?&]v=([^&]+)/);
      videoId = match ? match[1] : '';
    } else if (urlLower.includes('youtu.be/')) {
      const match = url.match(/youtu\.be\/([^?]+)/);
      videoId = match ? match[1] : '';
    }
    const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : undefined;
    return { platform: 'YouTube', color: '#FF0000', icon: <YouTubeIcon />, thumbnail };
  }
  
  // Spotify - use iframe embed
  if (urlLower.includes('spotify.com')) {
    return { platform: 'Spotify', color: '#1DB954', icon: <SpotifyIcon />, embedType: 'spotify', thumbnail: url };
  }
  
  // Bandcamp - use iframe embed
  if (urlLower.includes('bandcamp.com')) {
    return { platform: 'Bandcamp', color: '#629aa9', icon: <BandcampIcon />, embedType: 'bandcamp', thumbnail: url };
  }
  
  if (urlLower.includes('facebook.com') || urlLower.includes('fb.com')) {
    return { platform: 'Facebook', color: '#1877F2', icon: <FacebookIcon /> };
  }
  if (urlLower.includes('instagram.com')) {
    return { platform: 'Instagram', color: '#E4405F', icon: <InstagramIcon /> };
  }
  if (urlLower.includes('apple.com/music') || urlLower.includes('music.apple.com')) {
    return { platform: 'Apple Music', color: '#FA243C', icon: <AppleMusicIcon /> };
  }
  if (urlLower.startsWith('http://') || urlLower.startsWith('https://')) {
    return { platform: 'Website', color: '#666666', icon: <WebIcon /> };
  }
  
  return null;
};

const FormViewer = ({
  schema,
  values,
  onChange,
  disabled = false,
}: FormViewerProps) => {
  useEffect(() => {
    console.log('FormViewer rendering with values:', values);
    console.log('Schema fields:', schema.fields.map(f => ({ id: f.id, type: f.type })));
  }, [values, schema]);

  const renderField = (field: FormField) => {
    const fieldValue = values[field.id];
    console.log(`Rendering field ${field.id}:`, { 
      fieldValue,
      fieldType: field.type,
      fullValues: values 
    });
    
    // DEBUG: Check validation
    console.log('Field validation check:', {
      fieldId: field.id,
      label: field.label,
      hasValidation: !!field.validation,
      isRequired: field.validation?.required,
      fullField: field
    });

    const value = fieldValue || '';

    const commonProps = {
      id: field.id,
      name: field.id,
      value: value,
      onChange: (e: any) => {
        console.log(`Field ${field.id} change:`, e.target.value);
        onChange(field.id, e.target.value);
      },
      disabled,
      placeholder: field.placeholder,
      fullWidth: true,
      required: field.validation?.required,
      variant: 'outlined' as const,
      margin: 'dense' as const, // Changed to dense for better spacing in grid
    };

    const fieldLabel = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography 
          variant="subtitle1"
          dangerouslySetInnerHTML={{ 
            __html: (field.label || '') + 
                    (field.validation?.required ? '<span style="color: #d32f2f; margin-left: 4px;">*</span>' : '')
          }}
          sx={{
            '& p': { margin: 0, display: 'inline' },
            '& strong': { fontWeight: 'bold' },
            '& em': { fontStyle: 'italic' },
            '& a': { color: 'primary.main' }
          }}
        />
        {field.tooltip && (
          <Tooltip title={field.tooltip}>
            <IconButton size="small">
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );

    // Helper function to render rich text helper text
    const renderHelperText = (helperText?: string) => {
      if (!helperText) return undefined;
      return (
        <Box
          dangerouslySetInnerHTML={{ __html: helperText }}
          sx={{
            fontSize: '0.75rem',
            color: 'text.secondary',
            mt: 0.5,
            '& p': { margin: 0 },
            '& strong': { fontWeight: 'bold' },
            '& em': { fontStyle: 'italic' },
            '& a': { color: 'primary.main', textDecoration: 'underline' }
          }}
        />
      );
    };

    // Your existing switch statement for different field types
    switch (field.type) {
      case FieldType.Text:
      case FieldType.Email:
        return (
          <Box sx={{ height: '100%' }}>
            {fieldLabel}
            <TextField
              {...commonProps}
              type={field.type}
            />
            {renderHelperText(field.helperText)}
          </Box>
        );

      case FieldType.Url:
        const urlValue = value as string;
        const detectedPlatform = detectPlatform(urlValue);
        const showPreview = (field as any).showPreview !== false; // Default to true

        return (
          <Box sx={{ height: '100%' }}>
            {fieldLabel}
            <TextField
              {...commonProps}
              type="url"
            />
            {renderHelperText(field.helperText)}
            
            {/* Link Preview */}
            {showPreview && detectedPlatform && urlValue && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  border: '2px solid',
                  borderColor: detectedPlatform.color,
                  borderRadius: '8px',
                  backgroundColor: `${detectedPlatform.color}15`,
                }}
              >
                {detectedPlatform.embedType === 'spotify' ? (
                  // Spotify embed
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ color: detectedPlatform.color, display: 'flex', fontSize: '20px' }}>
                        {detectedPlatform.icon}
                      </Box>
                      <Typography variant="caption" sx={{ color: detectedPlatform.color, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '10px' }}>
                        {detectedPlatform.platform}
                      </Typography>
                    </Box>
                    <iframe
                      src={urlValue.replace('/track/', '/embed/track/').replace('/album/', '/embed/album/').replace('/artist/', '/embed/artist/')}
                      width="100%"
                      height="152"
                      frameBorder="0"
                      allow="encrypted-media"
                      style={{ borderRadius: '8px' }}
                    />
                  </Box>
                ) : detectedPlatform.embedType === 'bandcamp' ? (
                  // Bandcamp embed - convert to embed URL
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ color: detectedPlatform.color, display: 'flex', fontSize: '20px' }}>
                        {detectedPlatform.icon}
                      </Box>
                      <Typography variant="caption" sx={{ color: detectedPlatform.color, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '10px' }}>
                        {detectedPlatform.platform}
                      </Typography>
                    </Box>
                    <iframe
                      src={`https://bandcamp.com/EmbeddedPlayer/size=small/bgcol=ffffff/linkcol=0687f5/transparent=true/${urlValue.includes('/album/') ? 'album' : 'track'}=${urlValue.split('/').pop()}/`}
                      width="100%"
                      height="120"
                      frameBorder="0"
                      style={{ borderRadius: '8px' }}
                    />
                  </Box>
                ) : detectedPlatform.thumbnail ? (
                  // YouTube or other platforms with direct image thumbnail
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      component="img"
                      src={detectedPlatform.thumbnail}
                      alt={`${detectedPlatform.platform} thumbnail`}
                      sx={{
                        width: 120,
                        height: 90,
                        objectFit: 'cover',
                        borderRadius: '4px',
                        flexShrink: 0,
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <Typography variant="caption" sx={{ color: detectedPlatform.color, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '10px' }}>
                        {detectedPlatform.platform}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {urlValue}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  // Fallback to icon for platforms without thumbnail/embed
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ color: detectedPlatform.color, display: 'flex', alignItems: 'center', fontSize: '24px' }}>
                      {detectedPlatform.icon}
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <Typography variant="caption" sx={{ color: detectedPlatform.color, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '10px' }}>
                        {detectedPlatform.platform}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {urlValue}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );

      case FieldType.TextArea:
        return (
          <Box sx={{ height: '100%' }}>
            {fieldLabel}
            <TextField
              {...commonProps}
              multiline
              rows={4}
            />
            {renderHelperText(field.helperText)}
          </Box>
        );

      case FieldType.Select:
        if (!field.options || field.options.length === 0) {
          return (
            <Box sx={{ height: '100%' }}>
              {fieldLabel}
              <FormControl fullWidth variant="outlined" error>
                <Select
                  {...commonProps}
                  margin="none"
                  disabled
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>No options available</em>
                  </MenuItem>
                </Select>
                <FormHelperText>Error: No options provided</FormHelperText>
              </FormControl>
            </Box>
          );
        }
        return (
          <Box sx={{ height: '100%' }}>
            {fieldLabel}
            <FormControl fullWidth variant="outlined">
              <Select
                {...commonProps}
                margin="none"
                multiple={(field as any).multiple || false}
                value={(field as any).multiple ? (value || []) : value}
                onChange={(e) => {
                  console.log(`Field ${field.id} change:`, e.target.value);
                  onChange(field.id, e.target.value);
                }}
                displayEmpty
                renderValue={(selected) => {
                  if ((field as any).multiple) {
                    if (!selected || (selected as any[]).length === 0) {
                      return <em style={{ color: '#999' }}>Select options...</em>;
                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as any[]).map((val) => {
                          const option = field.options.find(opt => opt.value === val);
                          return (
                            <Box
                              key={val}
                              sx={{
                                backgroundColor: '#3A9188',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '16px',
                                fontSize: '14px',
                                fontWeight: 500
                              }}
                            >
                              {option?.label || val}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }
                  return selected;
                }}
              >
                {field.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {renderHelperText(field.helperText)}
          </Box>
        );

      case FieldType.Checkbox:
        return (
          <Box sx={{ height: '100%' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!value}
                  onChange={(e) => onChange(field.id, e.target.checked)}
                  disabled={disabled}
                />
              }
              label={
                <Box
                  dangerouslySetInnerHTML={{ __html: field.label || '' }}
                  sx={{
                    '& p': { margin: 0, display: 'inline' },
                    '& strong': { fontWeight: 'bold' },
                    '& em': { fontStyle: 'italic' },
                    '& a': { color: 'primary.main' }
                  }}
                />
              }
            />
            {renderHelperText(field.helperText)}
          </Box>
        );

      case FieldType.Sig:
        return (
          <Box sx={{ height: '100%' }}>
            {fieldLabel}
            <SignatureField
              config={
                field.signatureConfig || {
                  penColor: '#000000',
                  penSize: 2,
                  canvasHeight: 200,
                  backgroundColor: '#ffffff',
                  clearable: true,
                }
              }
              value={value as string}
              onChange={(newValue: string) => onChange(field.id, newValue)}
              disabled={disabled}
            />
            {renderHelperText(field.helperText)}
          </Box>
        );

      case FieldType.Upload:
        return (
          <Box sx={{ height: '100%' }}>
            {fieldLabel}
            <input
              type="file"
              accept={field.accept}
              multiple={field.multiple}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                onChange(field.id, field.multiple ? files : files[0]);
              }}
              disabled={disabled}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid rgba(0, 0, 0, 0.23)',
                borderRadius: '4px',
              }}
            />
            {renderHelperText(field.helperText)}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          {schema.title}
        </Typography>
        <form>
          <Grid 
            container 
            spacing={2} 
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              width: '100%',
              margin: '0 auto'
            }}
          >
            {schema.fields.map((field) => {
              // Calculate grid sizes with proper fallbacks
              const xs = field.grid?.xs ?? 12;
              const sm = field.grid?.sm ?? xs;
              const md = field.grid?.md ?? sm;
              const lg = field.grid?.lg ?? md;

              return (
                <Grid
                  item
                  xs={xs}
                  sm={sm}
                  md={md}
                  lg={lg}
                  key={field.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '80px', // Ensures consistent field heights
                    mb: 1 // Adds some bottom margin between fields
                  }}
                >
                  {renderField(field)}
                </Grid>
              );
            })}
          </Grid>
        </form>
      </Box>
    </Container>
  );
};

export default FormViewer;